import { useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/database';
import { soundEngine } from '../utils/soundEngine';
import { updateSettings } from '../db/settingsRepo';

export function useAudio() {
  const settings = useLiveQuery(() => db.settings.get('global_settings'));

  const soundEnabled = settings?.soundEnabled ?? true;
  const masterVolume = settings?.masterVolume ?? 0.75;

  useEffect(() => {
    soundEngine.setMuted(!soundEnabled);
    soundEngine.setVolume(masterVolume);
  }, [soundEnabled, masterVolume]);

  const toggleSound = useCallback(async () => {
    const nextState = !soundEnabled;
    await updateSettings({ soundEnabled: nextState });
    soundEngine.setMuted(!nextState);
  }, [soundEnabled]);

  const setVolume = useCallback(async (volume: number) => {
    const clamped = Math.max(0, Math.min(1, volume));
    await updateSettings({ masterVolume: clamped });
    soundEngine.setVolume(clamped);
  }, []);

  const playTick = useCallback((velocityRatio: number = 1) => {
    soundEngine.playTick(velocityRatio);
  }, []);

  const playSpinStart = useCallback(() => {
    soundEngine.playSpinStart();
  }, []);

  const playStopClack = useCallback(() => {
    soundEngine.playStopClack();
  }, []);

  const playWinnerFanfare = useCallback(() => {
    soundEngine.playWinnerFanfare();
  }, []);

  const playConfettiPop = useCallback(() => {
    soundEngine.playConfettiPop();
  }, []);

  const playButtonClick = useCallback(() => {
    soundEngine.playButtonClick();
  }, []);

  return {
    soundEnabled,
    masterVolume,
    toggleSound,
    setVolume,
    playTick,
    playSpinStart,
    playStopClack,
    playWinnerFanfare,
    playConfettiPop,
    playButtonClick,
  };
}
